import React, { useState, useEffect, useCallback, useRef } from 'react';

import * as Alert from 'components/alert';
import * as Page from 'components/page';
import { RHP } from 'providers';
import { useApi } from 'hooks';

import { OpenInnovationSection } from './OpenInnovationSection';

export function CorporateStep5({ useRankingCorporate, t }) {
    const { vars, putUserRankingApplication, putCompanyRankingApplication, updateSteps } = useRankingCorporate;
    const { application, companyApplication } = vars;
    const { steps } = application;
    const api = useApi();

    const [congresses, setCongresses] = useState([]);
    const [caseId, setCaseId] = useState('');
    const [selectedCongress, setSelectedCongress] = useState('');
    const [cases, setCases] = useState(companyApplication?.cases || []);
    const [submitting, setSubmitting] = useState(false);
    const [lastSaved, setLastSaved] = useState(null);
    const [saving, setSaving] = useState(false);
    const isFirstRender = useRef(true);

    // Fetch available congresses
    useEffect(() => {
        api.get('/congresses').then((res) => {
            if (Array.isArray(res)) setCongresses(res);
        }).catch(() => {});
    }, []);

    // Auto-select the only congress when there is just one option
    useEffect(() => {
        if (congresses.length === 1 && !selectedCongress) {
            setSelectedCongress(congresses[0]._id);
        }
    }, [congresses]);

    // Auto-save whenever cases change (skip first render)
    const saveCases = useCallback(async (casesToSave) => {
        setSaving(true);
        try {
            await putCompanyRankingApplication({
                cases: casesToSave.map((c) => ({
                    congressId: c.congressId,
                    caseId: c.caseId,
                    caseName: c.caseName || null,
                })),
            });

            const userPayload = {
                ...application,
                applied: !!application?.applied,
            };
            if (!userPayload.steps) userPayload.steps = {};
            if (casesToSave.length > 0) userPayload.steps.step5 = true;

            await putUserRankingApplication(userPayload);
            setLastSaved(new Date());
        } catch (err) {
            console.error(err);
        } finally {
            setSaving(false);
        }
    }, [application, putUserRankingApplication, putCompanyRankingApplication]);

    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }
        saveCases(cases);
    }, [cases]);

    // Check for previous step conclusion
    if (!steps || steps.step4 !== true) {
        RHP.replace('/ranking/latam/corporate/');
        return null;
    }

    const addCase = () => {
        if (!caseId.trim() || !selectedCongress) return;
        const alreadyExists = cases.some(
            (c) => c.caseId === caseId.trim() && c.congressId === selectedCongress
        );
        if (alreadyExists) return;

        const congress = congresses.find((c) => c._id === selectedCongress);
        setCases([
            ...cases,
            {
                congressId: selectedCongress,
                congressName: congress?.name || '',
                caseId: caseId.trim(),
            },
        ]);
        setCaseId('');
    };

    const removeCase = (index) => {
        setCases(cases.filter((_, i) => i !== index));
    };

    const onSubmit = async (ev) => {
        ev.preventDefault();
        setSubmitting(true);
        try {
            await saveCases(cases);
            RHP.push('/ranking/latam/corporate');
        } catch (err) {
            console.error(err);
        } finally {
            setSubmitting(false);
        }
    };

    const formatSavedDate = (date) => {
        if (!date) return '';
        return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
    };

    return (
        <Page.Page name="CorporateStep5" customBackText={t('glossary:backToRanking')}>
            <Page.Title>{t('CorporateStep5Cases.title')}</Page.Title>
            <Page.Descr>{t('CorporateStep5Cases.description')}</Page.Descr>

            <OpenInnovationSection
                companyId={vars.companyId}
                ranking={vars.ranking.name}
                companyName={vars.companyDetails?.name}
                t={t}
            />

            <Page.Ruler />
            <p style={{ fontWeight: 600, color: '#777', marginBottom: 4 }}>
                {t('CorporateStep5OpenInnovation.legacySectionTitle')}
            </p>

            {cases.length === 0 && (
                <Alert.AlertRow
                    icons="far fa-info-circle"
                    type="warning"
                    descr={t('CorporateStep5Cases.emptyStateAlert')}
                />
            )}

            <form onSubmit={onSubmit}>
                <p style={{ fontWeight: 500, marginTop: 24, marginBottom: 16 }}>
                    {t('CorporateStep5Cases.formInstruction')}
                </p>

                <div style={{ marginBottom: 20 }}>
                    <label style={{ display: 'block', fontWeight: 'bold', marginBottom: 8 }}>
                        {t('CorporateStep5Cases.caseIdLabel')}
                    </label>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'stretch' }}>
                        <input
                            type="text"
                            value={caseId}
                            onChange={(e) => setCaseId(e.target.value)}
                            placeholder={t('CorporateStep5Cases.caseIdPlaceholder')}
                            style={{
                                flex: 1,
                                minWidth: 0,
                                padding: '8px 12px',
                                border: '1px solid #ccc',
                                borderRadius: 4,
                            }}
                        />
                        <button
                            type="button"
                            onClick={addCase}
                            disabled={!caseId.trim() || !selectedCongress}
                            style={{
                                padding: '8px 16px',
                                background: !caseId.trim() || !selectedCongress ? '#cfd5dc' : '#007bff',
                                color: '#fff',
                                border: 'none',
                                borderRadius: 4,
                                cursor: !caseId.trim() || !selectedCongress ? 'not-allowed' : 'pointer',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            + {t('CorporateStep5Cases.addCase')}
                        </button>
                    </div>
                    <p style={{ fontSize: '0.85em', color: '#666', marginTop: 6, marginBottom: 0 }}>
                        {t('CorporateStep5Cases.caseIdHelp')}
                    </p>

                    {congresses.length > 1 && (
                        <>
                            <label
                                style={{
                                    display: 'block',
                                    fontWeight: 'bold',
                                    marginTop: 16,
                                    marginBottom: 8,
                                }}
                            >
                                {t('CorporateStep5Cases.congressLabel')}
                            </label>
                            <select
                                value={selectedCongress}
                                onChange={(e) => setSelectedCongress(e.target.value)}
                                style={{
                                    width: '100%',
                                    padding: '8px 12px',
                                    border: '1px solid #ccc',
                                    borderRadius: 4,
                                }}
                            >
                                <option value="">{t('CorporateStep5Cases.selectCongress')}</option>
                                {congresses.map((c) => (
                                    <option key={c._id} value={c._id}>
                                        {c.name}
                                    </option>
                                ))}
                            </select>
                        </>
                    )}
                </div>

                {cases.length > 0 && (
                    <p style={{ fontSize: '0.9em', color: '#666', marginTop: 4, marginBottom: 24 }}>
                        {t('CorporateStep5Cases.hasCasesHelp')}{' '}
                        <a
                            href="https://100os.net/SubmetaSeuAbstract"
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: '#007bff', textDecoration: 'underline' }}
                        >
                            100os.net/SubmetaSeuAbstract
                        </a>
                    </p>
                )}

                {cases.length > 0 && (
                    <div style={{ marginTop: 16 }}>
                        <label style={{ fontWeight: 'bold', marginBottom: 8, display: 'block' }}>
                            {t('CorporateStep5Cases.submittedCases')}
                        </label>
                        {cases.map((c, index) => {
                            const congress = congresses.find((cg) => cg._id === c.congressId);
                            const congressName =
                                congress?.name || c.congressName || c.congressId;
                            return (
                                <div
                                    key={index}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        padding: '12px 14px',
                                        border: '1px solid #eee',
                                        borderRadius: 4,
                                        marginBottom: 6,
                                    }}
                                >
                                    <div>
                                        <div style={{ fontWeight: 700 }}>
                                            {t('CorporateStep5Cases.workNumberPrefix')}
                                            {c.caseId}
                                            {c.caseName && (
                                                <span style={{ fontWeight: 400 }}>
                                                    {' — '}
                                                    {c.caseName}
                                                </span>
                                            )}
                                        </div>
                                        <div style={{ fontSize: '0.85em', color: '#777' }}>
                                            {congressName}
                                        </div>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => removeCase(index)}
                                        style={{
                                            background: 'none',
                                            border: 'none',
                                            color: '#dc3545',
                                            cursor: 'pointer',
                                            fontSize: 20,
                                        }}
                                        aria-label={t('glossary:delete')}
                                    >
                                        &times;
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                )}

                <Page.Ruler />

                <div style={{ marginBottom: 20 }}>
                    <p
                        style={{
                            fontWeight: 600,
                            fontSize: '1.05em',
                            color: '#444',
                            marginBottom: 8,
                        }}
                    >
                        {t('CorporateStep5Cases.bonusTitle')}
                    </p>
                    <div
                        style={{ fontSize: '0.9em', color: '#555' }}
                        dangerouslySetInnerHTML={{
                            __html: t('CorporateStep5Cases.bonusDescription'),
                        }}
                    />
                </div>

                <div style={{ marginBottom: 20 }}>
                    <p
                        style={{
                            fontWeight: 600,
                            fontSize: '1.05em',
                            color: '#444',
                            marginBottom: 8,
                        }}
                    >
                        {t('CorporateStep5Cases.congressInfoTitle')}
                    </p>
                    <div
                        style={{ fontSize: '0.9em', color: '#555' }}
                        dangerouslySetInnerHTML={{
                            __html: t('CorporateStep5Cases.congressInfoDescription'),
                        }}
                    />
                </div>

                <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
                    <Page.Buttn
                        style={{ width: '20%' }}
                        // @ts-ignore
                        to="/ranking/latam/corporate/step4"
                    >
                        <span className="icon far fa-long-arrow-left" aria-hidden="true" />
                        <span className="text">{t('glossary:back')}</span>
                    </Page.Buttn>

                    <Page.Buttn
                        style={{ width: '80%' }}
                        // @ts-ignore
                        to="/ranking/latam/corporate"
                        onClick={() => {
                            if (steps.step4 && !steps.step5) updateSteps({ step5: true });
                        }}
                    >
                        <span className="text">{t('glossary:continue')}</span>
                        <span className="icon far fa-long-arrow-right" aria-hidden="true" />
                    </Page.Buttn>
                </div>

                {(lastSaved || saving) && (
                    <p style={{ fontSize: 12, color: '#aaa', textAlign: 'center', marginTop: 12 }}>
                        {saving
                            ? 'Salvando...'
                            : `Salvo automaticamente em ${formatSavedDate(lastSaved)}`}
                    </p>
                )}
            </form>
        </Page.Page>
    );
}
